// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import Dialog from '@material-ui/core/Dialog';
import Divider from '@material-ui/core/Divider';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import Slide from '@material-ui/core/Slide';
import { Flex, Box, useSystem, useResponsive } from 'react-system';
import * as icons from '../../icons';
import { useLocale } from '../../hooks/locale';
import { useForceIOSToolbar, useDisableIOSBodyScroll } from '../../hooks/ios';
import { useTheme } from '../../theme';
import { MappingImage } from './MappingImage';
import { DevelopmentEnquiry, EnquirySent } from './DevelopmentEnquiry';
import { DevelopmentProductDetails } from './DevelopmentProductDetails';

import type { DevelopmentProductSelectionDialog_development$key } from './__generated__/DevelopmentProductSelectionDialog_development.graphql';
import { useSubmitRegistration } from './useSubmitRegistration';

type ContentProps = {|
  onClose: () => void,
  productId: string,
  development: DevelopmentProductSelectionDialog_development$key,
|};

type Props = {|
  dialogOpen: boolean,
  animation: 'left' | 'up',
  ...ContentProps,
|};

function UpTransition(props) {
  return <Slide direction="up" {...props} />;
}

function LeftTransition(props) {
  return <Slide direction="left" {...props} />;
}

const AlterContent = ({ contentA, contentB, showB }) => {
  return (
    <div css={{ width: '100%', overflow: 'hidden' }}>
      <Flex
        width="200%"
        css={{
          transform: showB ? 'translateX(-50%)' : 'translateX(0)',
          transition: 'transform .4s',
        }}
      >
        <Box width={1 / 2}>{contentA}</Box>
        <Box width={1 / 2}>{contentB}</Box>
      </Flex>
    </div>
  );
};

const BottomButtons = ({
  development,
  enquirySent,
  product,
  setShowEnquiry,
  showEnquiry,
  submitRegistration,
  submitEnquiry,
}) => {
  const { t } = useLocale();
  const responsive = useResponsive();

  if (development.registrationType === 'complete_registration') {
    return (
      <Box px={[3, 4]}>
        <Button
          color="primary"
          size={responsive(['small', 'medium'])}
          disabled={product.status === 'available' ? false : true}
          onClick={submitRegistration}
        >
          {t('register')}
        </Button>
      </Box>
    );
  }

  if (enquirySent) {
    return (
      <Box px={[3, 4]}>
        <Button
          color="primary"
          variant="outlined"
          size={responsive(['small', 'medium'])}
          onClick={close}
        >
          {t('backToListing')}
        </Button>
      </Box>
    );
  }

  return (
    <AlterContent
      contentA={
        <Box px={[3, 4]}>
          <Button
            color="primary"
            size={responsive(['small', 'medium'])}
            disabled={product.status === 'available' ? false : true}
            onClick={() => setShowEnquiry(true)}
          >
            <Flex alignItems="center">
              <Box flexGrow={1}>{t('requestMoreInfo')}</Box>
              <icons.ArrowForward size={24} />
            </Flex>
          </Button>
        </Box>
      }
      contentB={
        <Flex mx={-2} px={[3, 4]}>
          <Box width={1 / 2} px={2}>
            <Button
              color="primary"
              variant="outlined"
              size={responsive(['small', 'medium'])}
              onClick={() => setShowEnquiry(false)}
            >
              {t('back')}
            </Button>
          </Box>
          <Box width={1 / 2} px={2} pt="1px">
            <Button
              color="primary"
              size={responsive(['small', 'medium'])}
              onClick={submitEnquiry}
            >
              <Flex alignItems="center">
                <Box flexGrow={1}>{t('send')}</Box>
                <icons.ArrowForward size={24} />
              </Flex>
            </Button>
          </Box>
        </Flex>
      }
      showB={showEnquiry}
    />
  );
};

export const Content = (props: ContentProps): React.Node => {
  const { text } = useTheme();
  const { media } = useSystem();
  const [showEnquiry, setShowEnquiry] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [productId, setProductId] = React.useState(props.productId);
  const enquiryFormRef = React.useRef(null);

  const close = () => {
    setSent(false);
    setShowEnquiry(false);
    setProductId(props.productId);
    props.onClose();
  };

  const [submitRegistration] = useSubmitRegistration();

  const development = useFragment(
    graphql`
      fragment DevelopmentProductSelectionDialog_development on DevelopmentProduct {
        ...DevelopmentEnquiry_development
        id
        mappingImage {
          ...MappingImage_image
        }
        currentUserRegistrationForm {
          id
          product {
            id
          }
        }
        registrationType
        products {
          ...DevelopmentProductDetails_product
          ...MappingImage_products
          id
          lotReference
          status
        }
      }
    `,
    props.development,
  );

  const product = development.products.find(x => x.id === productId);

  // just for Flow
  if (product == null) {
    return null;
  }

  const bottomButtons = (
    <BottomButtons
      development={development}
      product={product}
      setShowEnquiry={setShowEnquiry}
      showEnquiry={showEnquiry}
      submitEnquiry={() => {
        if (enquiryFormRef.current != null) {
          enquiryFormRef.current.submitForm();
        }
      }}
      enquirySent={sent}
      submitRegistration={() => {
        const currentProductId =
          development.currentUserRegistrationForm?.product?.id;

        submitRegistration(
          {
            id: development.currentUserRegistrationForm?.id,
            // Replace productId if already set,
            // if no - we will just prefill it and not update yet
            productId: currentProductId != null ? product.id : currentProductId,
            developmentProductId: development.id,
          },
          product.id,
        );
      }}
    />
  );

  return (
    <>
      <Box css={media({ display: ['block', 'none'] })}>
        <AppBar color="default">
          <Toolbar>
            <IconButton color="inherit" onClick={close} aria-label="Close">
              <icons.ArrowBack size={28} />
            </IconButton>
            <Box width={1} css={[text.h6, { textAlign: 'center' }]}>
              {product.lotReference}
            </Box>
            <Box width={'52px'} height={'52px'} flexShrink={0} />
          </Toolbar>
        </AppBar>
      </Box>

      <DialogContent css={{ padding: 0, overflow: 'unset' }}>
        <Flex
          flexDirection="column"
          justifyContent="space-between"
          css={{ minHeight: '100%' }}
        >
          <Box py={['58px', 0]}>
            {development.mappingImage && (
              <MappingImage
                products={[product]}
                image={development.mappingImage}
              />
            )}
            <Box mt="24px">
              {sent ? (
                <Box px={[3, 4]}>
                  <EnquirySent />
                </Box>
              ) : (
                <AlterContent
                  contentA={
                    <Box px={[3, 4]}>
                      <DevelopmentProductDetails product={product} />
                    </Box>
                  }
                  contentB={
                    <Box px={[3, 4]}>
                      <DevelopmentEnquiry
                        showSubmit={false}
                        development={development}
                        selectedProductId={product.id}
                        formRef={enquiryFormRef}
                        onProductSelect={setProductId}
                        onSuccessfulSubmit={() => setSent(true)}
                      />
                    </Box>
                  }
                  showB={showEnquiry}
                />
              )}
            </Box>
          </Box>

          <Box py={4} css={media({ display: ['none', 'block'] })}>
            {bottomButtons}
          </Box>

          <AppBar
            color="inherit"
            css={[
              { top: 'auto', bottom: 0 },
              media({ display: ['block', 'none'] }),
            ]}
          >
            <Divider />
            <Box py={2}>{bottomButtons}</Box>
          </AppBar>
        </Flex>
      </DialogContent>
    </>
  );
};

export const DevelopmentProductSelectionDialog = ({
  dialogOpen,
  animation,
  ...other
}: Props): React.Node => {
  const responsive = useResponsive();
  useForceIOSToolbar(dialogOpen ? false : null);
  useDisableIOSBodyScroll(dialogOpen);

  return (
    <Dialog
      open={dialogOpen}
      fullScreen={responsive([true, false])}
      onClose={other.onClose}
      scroll="body"
      css={{ padding: '0' }}
      TransitionComponent={animation === 'up' ? UpTransition : LeftTransition}
    >
      {/* 
      We use key here to reset Content's state every time user opens dialog.
      (React recreates component instance if key changes)
      */}
      <Content {...other} key={other.productId} />
    </Dialog>
  );
};
